import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import TradingChart from './TradingChart';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useBinancePrices, type TradeItem } from '@/hooks/useBinancePrices';

export default function HeroSection() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { prices, connected, generateTrade } = useBinancePrices();
  const [trades, setTrades] = useState<TradeItem[]>([
    { pair: 'BTC-PERP', side: 'long', price: 45230.5, size: 2.5, time: '2s ago', id: 1 },
    { pair: 'ETH-PERP', side: 'short', price: 3124.8, size: 5.0, time: '5s ago', id: 2 },
    { pair: 'SOL-PERP', side: 'long', price: 142.3, size: 10.0, time: '8s ago', id: 3 },
    { pair: 'ARB-PERP', side: 'long', price: 0.89, size: 2500.0, time: '12s ago', id: 4 },
    { pair: 'DOGE-PERP', side: 'short', price: 0.12, size: 50000.0, time: '15s ago', id: 5 },
  ]);
  const timeUpdateRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Every 3 seconds, push a new trade to the top
  useEffect(() => {
    const interval = setInterval(() => {
      const newTrade = generateTrade();
      setTrades((prev) => {
        const updated = [
          newTrade,
          ...prev.map((t) => ({ ...t, time: incrementTime(t.time) })),
        ].slice(0, 8);
        return updated;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [generateTrade]);

  // Update displayed prices when real-time prices change
  useEffect(() => {
    setTrades((prev) =>
      prev.map((trade) => ({
        ...trade,
        price: prices[trade.pair] || trade.price,
      }))
    );
  }, [prices]);

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#666]' : 'text-[#888]';
  const textDim = isDark ? 'text-[#444]' : 'text-[#bbb]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const bgWhite5 = isDark ? 'bg-white/5' : 'bg-black/5';
  const bgWhite10 = isDark ? 'bg-white/10' : 'bg-black/10';
  const hoverBgWhite5 = isDark ? 'hover:bg-white/5' : 'hover:bg-black/5';
  const hoverBgWhite10 = isDark ? 'hover:bg-white/10' : 'hover:bg-black/10';
  const hoverTextWhite = isDark ? 'hover:text-white' : 'hover:text-[#111]';
  const borderWhite10 = isDark ? 'border-white/10' : 'border-black/10';
  const borderWhite20 = isDark ? 'border-white/20' : 'border-black/20';

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full flex items-center overflow-hidden pt-20"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className={`absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[128px] ${isDark ? 'bg-[#00ff88]/5' : 'bg-[#00ff88]/8'}`} />
      <div className={`absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-[100px] ${isDark ? 'bg-[#00ff88]/3' : 'bg-[#00ff88]/6'}`} />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-12 md:py-20">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          {/* Left Content */}
          <div className="flex-1 max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00ff88]/10 border border-[#00ff88]/20 rounded-full mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" />
                <span className="text-xs text-[#00ff88] font-mono tracking-wide">{t('hero.badge')}</span>
              </div>

              <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight ${textPrimary}`}>
                <span className={textPrimary}>{t('hero.title1')}</span>
                <br />
                <span className="gradient-text">{t('hero.title2')}</span>
              </h1>

              <p className={`mt-6 text-base md:text-lg leading-relaxed max-w-xl ${textSecondary}`}>
                {t('hero.subtitle')}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#features"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#00ff88] text-[#050505] font-semibold text-sm rounded-lg hover:bg-[#00cc6a] transition-colors whitespace-nowrap"
                >
                  {t('hero.startTrading')}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </a>
                <a
                  href="https://claw.zhisu.ai/"
                  className={`inline-flex items-center gap-2 px-6 py-3 border font-medium text-sm rounded-lg transition-all whitespace-nowrap ${bgWhite5} ${borderWhite10} ${textPrimary} ${hoverBgWhite10} ${borderWhite20}`}
                >
                  <i className="ri-robot-2-line w-4 h-4 flex items-center justify-center" />
                  {t('hero.launchAgent')}
                </a>
                <a
                  href="#developers"
                  className={`inline-flex items-center gap-2 px-6 py-3 bg-transparent font-medium text-sm rounded-lg transition-colors whitespace-nowrap ${textSecondary} ${hoverTextWhite}`}
                >
                  <i className="ri-code-box-line w-4 h-4 flex items-center justify-center" />
                  {t('hero.apiDocs')}
                </a>
              </div>

              {/* Quick stats row */}
              <div className="mt-10 flex flex-wrap gap-6 md:gap-10">
                <div>
                  <div className={`text-2xl md:text-3xl font-bold font-mono ${textPrimary}`}>$2.8B+</div>
                  <div className={`text-xs uppercase tracking-wider mt-1 ${textMuted}`}>{t('hero.volume24h')}</div>
                </div>
                <div>
                  <div className={`text-2xl md:text-3xl font-bold font-mono ${textPrimary}`}>145K+</div>
                  <div className={`text-xs uppercase tracking-wider mt-1 ${textMuted}`}>{t('hero.activeTraders')}</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold font-mono text-[#00ff88]">99.99%</div>
                  <div className={`text-xs uppercase tracking-wider mt-1 ${textMuted}`}>{t('hero.uptime')}</div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Content - Trading Dashboard */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex-1 w-full max-w-xl lg:max-w-none"
          >
            <div className="glass-card p-4 md:p-5 relative overflow-hidden">
              {/* Terminal header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff4444]/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ffaa00]/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00ff88]/80" />
                  </div>
                  <span className={`text-xs font-mono ml-2 ${textMuted}`}>{t('hero.terminalName')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="status-dot live" />
                  <span className="text-xs text-[#00ff88] font-mono">{t('hero.live')}</span>
                </div>
              </div>

              {/* Chart */}
              <div className="h-[260px] md:h-[300px]">
                <TradingChart />
              </div>

              {/* Bottom info bar */}
              <div className={`mt-4 pt-3 border-t ${borderLight} flex flex-wrap items-center justify-between gap-3 text-xs font-mono ${textMuted}`}>
                <div className="flex items-center gap-4">
                  <span>OI: $892.4M</span>
                  <span>{t('hero.funding')}: 0.012%</span>
                  <span>{t('hero.spread')}: 0.02%</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-flashlight-line w-3 h-3 flex items-center justify-center text-[#00ff88]" />
                  <span className="text-[#00ff88]">{t('hero.aiActive')}</span>
                </div>
              </div>
            </div>

            {/* Live trades feed */}
            <div className="mt-3 glass-card p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-mono uppercase tracking-wider ${textMuted}`}>{t('hero.liveTrades')}</span>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${connected ? 'bg-[#00ff88]/10 text-[#00ff88]' : 'bg-[#ff4444]/10 text-[#ff4444]'}`}>
                    <span className={`w-1 h-1 rounded-full ${connected ? 'bg-[#00ff88] animate-pulse' : 'bg-[#ff4444]'}`} />
                    {connected ? t('hero.realtime') : 'WS OFF'}
                  </span>
                </div>
                <span className={`text-[10px] font-mono ${textMuted}`}>Binance Stream</span>
              </div>
              <div className="space-y-1.5 max-h-[150px] overflow-hidden">
                {trades.map((trade, index) => (
                  <motion.div
                    key={trade.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between text-xs font-mono"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-1 h-1 rounded-full ${trade.side === 'long' ? 'bg-[#00ff88]' : 'bg-[#ff4444]'}`} />
                      <span className={textSecondary}>{trade.pair}</span>
                      <span className={trade.side === 'long' ? 'text-[#00ff88]' : 'text-[#ff4444]'}>
                        {t(`hero.${trade.side}`)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`${textPrimary} tabular-nums`}>
                        ${formatPrice(trade.price)}
                      </span>
                      <span className={textMuted}>{trade.size.toFixed(2)}x</span>
                      <span className={textDim}>{trade.time}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function formatPrice(price: number): string {
  if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1) return price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return price.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 });
}

function incrementTime(timeStr: string): string {
  if (timeStr === 'just now') return '1s ago';
  const match = timeStr.match(/(\d+)s ago/);
  if (match) {
    const seconds = parseInt(match[1], 10) + 3;
    if (seconds >= 60) return '1m ago';
    return `${seconds}s ago`;
  }
  const minMatch = timeStr.match(/(\d+)m ago/);
  if (minMatch) {
    return `${parseInt(minMatch[1], 10) + 1}m ago`;
  }
  return timeStr;
}